import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { boronClient } from "../../api";
import { Progress } from "../../domains/DrillProgress";
import { createError, HTTPErrors } from "../../errors";
import { paths } from "../../lib/api/v1";

type Props = {
  sectionId: string;
  studyRecordIds: readonly string[];
  enabled: boolean;
  /**
   * 同一 section 内で複数インスタンスを起動する呼び出し側（例: タイムラインサマリーの生徒単位の TimelineSummaryItem）では必須。
   * 指定するとキャッシュが生徒単位に分離され、enabled=false のインスタンスの cleanup が他生徒のキャッシュを巻き込まない。
   * 単一インスタンスでしか使わない呼び出し側（例: SectionsTimelinePage）では省略可。
   */
  studentId?: string;
};

const URL = "/api/v1/sections/{section_id}/timeline/drill_progress";
type Url = typeof URL;

type DrillProgressResponse =
  paths[Url]["get"]["responses"]["200"]["content"]["application/json"];

export type DrillProgress = NonNullable<
  DrillProgressResponse["studySessionProgresses"]
>[0]["progress"];

/**
 * タイムラインのドリル進捗情報を取得するフック
 *
 * ## 概要
 * タイムラインのドリル学習記録に対する進捗情報（定着度バー）を取得します。
 *
 * ## 最適化の仕組み
 * ページング時の重複API呼び出しを防ぐため、以下の戦略を採用しています：
 * - 既に取得済みのIDを`useRef`で追跡
 * - 新しいIDのみをフィルタリングしてAPIリクエスト
 * - React Queryのキャッシュに結果をマージして一元管理
 *
 * これにより、タイムラインをページングする際に無駄なAPIコールを削減し、
 * パフォーマンスを向上させます。
 *
 * @param sectionId - 校舎ID
 * @param studyRecordIds - 学習記録IDの配列（ドリル教材のみ）
 * @param enabled - フックを有効化するかどうか
 * @param studentId - 生徒ID（同一 section 内で複数インスタンス起動する場合に必須。キャッシュを生徒単位に分離する）
 * @returns React Queryのクエリ結果（data: Map<学習記録ID, Progress>）
 */
export const useFetchTimelineDrillProgress = ({
  sectionId,
  studyRecordIds,
  enabled,
  studentId,
}: Props) => {
  const queryClient = useQueryClient();
  // 既に取得済みのIDを保持
  const fetchedIdsRef = useRef<Set<string>>(new Set());
  // 前回のstudyRecordIdsを保持（変更検知用）
  const prevStudyRecordIdsRef = useRef<string>("");

  // 同一 section 内で複数インスタンスがある場合、studentId で queryKey を分離する
  // （分離しないと enabled=false のインスタンスの cleanup が他生徒のキャッシュを巻き込む）
  const queryKey = studentId ? [URL, sectionId, studentId] : [URL, sectionId];

  // studyRecordIdsの変化を検知（配列の内容ベースで比較）
  const studyRecordIdsKey = [...studyRecordIds].sort().join(",");

  // enabledがfalseになったらキャッシュと内部状態をクリア
  // （プロプランからフリープランに切り替えた際にキャッシュが残るのを防ぐ）
  useEffect(() => {
    if (!enabled) {
      queryClient.removeQueries({ queryKey });
      fetchedIdsRef.current.clear();
      prevStudyRecordIdsRef.current = "";
    }
  }, [enabled, sectionId, studentId, queryClient]);

  useEffect(() => {
    // studyRecordIdsが変わったか確認
    if (
      !enabled ||
      studyRecordIdsKey === prevStudyRecordIdsRef.current ||
      studyRecordIds.length === 0
    ) {
      return;
    }

    prevStudyRecordIdsRef.current = studyRecordIdsKey;

    // 新しいIDのみを抽出
    const newStudyRecordIds = studyRecordIds.filter(
      (id) => !fetchedIdsRef.current.has(id),
    );

    if (newStudyRecordIds.length === 0) {
      return;
    }

    // 新しいIDのデータを取得してキャッシュに反映
    const fetchAndMerge = async () => {
      try {
        const newProgressMap = await fetchTimelineDrillProgress({
          sectionId,
          studyRecordIds: newStudyRecordIds,
        });

        // 新しく取得したIDを記録
        newStudyRecordIds.forEach((id) => fetchedIdsRef.current.add(id));

        // 既存のキャッシュデータを取得
        const existingData =
          queryClient.getQueryData<Map<string, Progress>>(queryKey);

        // 既存のデータと新しいデータをマージ
        const mergedData = new Map(existingData || []);
        newProgressMap.forEach((value, key) => {
          mergedData.set(key, value);
        });

        // キャッシュを更新
        queryClient.setQueryData(queryKey, mergedData);
      } catch (error) {
        // エラーハンドリングはReact Queryに任せる
        console.error("Failed to fetch drill progress:", error);
      }
    };

    fetchAndMerge();
  }, [enabled, studyRecordIdsKey, sectionId, studentId, queryClient]);

  // キャッシュからデータを取得して返す
  const result = useQuery<Map<string, Progress>, HTTPErrors>({
    queryKey,
    queryFn: () => {
      // データはuseEffectで更新されるため、初期状態として空のMapを返す
      return Promise.resolve(new Map());
    },
    enabled,
    // データはuseEffect経由でsetQueryDataにより手動管理しているため、
    // React Queryの自動リフェッチを無効化する（タブ復帰時にqueryFnの空Mapで上書きされるのを防ぐ）
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return result;
};

const fetchTimelineDrillProgress = async ({
  sectionId,
  studyRecordIds,
}: {
  sectionId: string;
  studyRecordIds: readonly string[];
}) => {
  const { data, response } = await boronClient.GET(URL, {
    params: {
      path: { section_id: sectionId },
      query: {
        study_record_ids: studyRecordIds,
      },
    },
  });

  if (response.ok && data) {
    // レスポンス配列をMapに変換してO(1)検索を実現
    // API型（DrillProgress）からドメイン型（Progress）に変換
    const progressMap = new Map<string, Progress>(
      data.studySessionProgresses
        ?.filter((sp) => sp.progress !== undefined)
        .map((sp) => [
          String(sp.studyRecordId),
          {
            notStudiedCount: sp.progress!.notStudiedCount ?? 0,
            studyingCount: sp.progress!.studyingCount ?? 0,
            establishedCount: sp.progress!.establishedCount ?? 0,
            perfectCount: sp.progress!.perfectCount ?? 0,
            studiedCount: sp.progress!.studiedCount ?? 0,
          },
        ]) ?? [],
    );
    return progressMap;
  }

  throw await createError(response);
};
